import { cookieProfil } from "../../Datalayers/features/cookieProfil";

/**
 * Redirection configurateur.
 *
 * Dans le tunnel de devis (Gravity Form non-ajax), à l'étape 2, si une case
 * cochée a pour label « Base vie roulante », le clic sur « Suivant » n'envoie
 * pas le formulaire mais ouvre le configurateur weapzy dans un nouvel onglet.
 * Les UTM stockés en cookie sont repris, et utm_term est forcé à « configurateur ».
 *
 * FEATURE FLAG (cf. ACTIVATION_CLASS) : la bascule reste INACTIVE tant que la classe
 * marqueur n'est pas présente dans le formulaire. On peut donc livrer ce code en
 * amont : le jour de la MEP, le chef de projet active la fonctionnalité depuis
 * l'éditeur Gravity Forms (champ « CSS Class ») sans nouvelle mise en production,
 * et la désactive de la même façon en cas de souci (rollback sans dev).
 */

// URL du configurateur. Surchargeable via data-configurator-url sur .template-request-quote.
const CONFIGURATOR_BASE_URL = 'https://modeal.weapzy.com/';

// Feature flag : classe à poser UNE SEULE FOIS dans le formulaire (idéalement la « Classe CSS »
// d'un saut de page, cf. onglet Apparence dans Gravity Forms). Toutes les pages GF étant
// présentes dans le DOM, on la détecte à l'échelle du formulaire, peu importe où elle est placée.
const ACTIVATION_CLASS = 'js-configurator-active';

// Label de la case qui déclenche la bascule (comparé normalisé : minuscule, espaces réduits).
const TARGET_CHOICE_LABEL = 'base vie roulante';

const normalize = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Vrai si une des cases cochées de la page a pour label (ou valeur) « Base vie roulante ».
 */
const isTargetChoiceChecked = (page: HTMLElement): boolean => {
    const checked = page.querySelectorAll<HTMLInputElement>('.gfield-choice-input:checked');

    return Array.from(checked).some(input => {
        const labelText = input.closest('.gchoice')?.querySelector('label')?.textContent ?? '';
        return normalize(labelText) === TARGET_CHOICE_LABEL || normalize(input.value) === TARGET_CHOICE_LABEL;
    });
};

/**
 * Construit l'URL du configurateur : utm_term=configurateur (forcé) + les UTM présents en cookie.
 */
const buildConfiguratorUrl = (baseUrl: string): string => {
    const params = new URLSearchParams();
    params.set('utm_term', 'configurateur');

    (['utm_source', 'utm_medium', 'utm_campaign'] as const).forEach(key => {
        const value = cookieProfil.getCookie(key);
        if (value) {
            params.set(key, value);
        }
    });

    return `${baseUrl}?${params.toString()}`;
};

/**
 * Ouvre le configurateur dans un nouvel onglet.
 * Appelé de façon synchrone dans le handler de clic pour rester un geste
 * utilisateur (sinon le bloqueur de pop-up empêcherait l'ouverture).
 */
const openConfigurator = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
};

export const configuratorRedirect = (): void => {
    const page = document.querySelector<HTMLElement>('.template-request-quote');
    if (!page) return;

    const form = page.querySelector<HTMLFormElement>('.gform_wrapper form');
    if (!form) return;

    const baseUrl = page.dataset.configuratorUrl?.trim() || CONFIGURATOR_BASE_URL;

    // Délégation en capture phase : on intercepte AVANT que Gravity Forms ne soumette la page.
    form.addEventListener('click', event => {
        const nextButton = (event.target as HTMLElement | null)?.closest('[data-submission-type="next"]');
        if (!nextButton) return;

        // Feature flag : inactif tant que la classe marqueur n'est nulle part dans le formulaire.
        // Recherche à l'échelle du formulaire (toutes les pages GF sont dans le DOM), donc le
        // chef de projet peut la poser une seule fois où il veut. Activation/rollback côté GF.
        const featureEnabled = form.classList.contains(ACTIVATION_CLASS)
            || form.querySelector(`.${ACTIVATION_CLASS}`) !== null;
        if (!featureEnabled) return;

        // Uniquement à l'étape 2.
        const activeStep = document.querySelector('.gf_step_active .gf_step_number')?.textContent?.trim();
        if (activeStep !== '2') return;

        const currentPage = nextButton.closest<HTMLElement>('.gform_page');
        if (!currentPage || !isTargetChoiceChecked(currentPage)) return;

        // Bloque la soumission GF (case Base vie roulante cochée) et bascule vers le configurateur.
        event.preventDefault();
        event.stopImmediatePropagation();
        // NB : suspend volontairement l'event dataLayer « Etape2 » (abandon du tunnel standard).
        // TODO (tracking, cf. MOD-95) : pousser un event dataLayer dédié si validé côté client.
        openConfigurator(buildConfiguratorUrl(baseUrl));
    }, true);
};
